/**
 * Notification Content Builder
 * Creates value-delivering notification content from health analysis
 */

import { 
  HealthConcern, 
  HealthAlertSeverity, 
  HealthAlertDetails,
  DocumentEventType 
} from '../types/notifications';
import { logger } from './logger';

const log = logger.createScope('NotificationContentBuilder');

export class NotificationContentBuilder {
  /**
   * Build health alert title with actionable information
   */
  static buildHealthAlertTitle(
    petName: string, 
    severity: HealthAlertSeverity,
    concerns: HealthConcern[]
  ): string {
    if (severity === 'critical') {
      return `🚨 ${petName} needs immediate attention`;
    }
    
    if (severity === 'urgent') {
      return `⚠️ ${petName} has concerning lab results`;
    }
    
    if (concerns.length === 1) {
      const concern = concerns[0];
      return `${petName}'s ${concern.metric} needs monitoring`;
    }
    
    if (concerns.length === 2) {
      return `${petName}'s ${concerns[0].metric} and ${concerns[1].metric} need monitoring`;
    }
    
    return `${petName} has ${concerns.length} values to monitor`;
  }

  /**
   * Build health alert body with specific insights and trends
   */
  static buildHealthAlertBody(concerns: HealthConcern[]): string {
    if (concerns.length === 0) {
      return 'Lab results have been processed. Review the details.';
    }
    
    if (concerns.length === 1) {
      return this.buildSingleConcernBody(concerns[0]);
    }
    
    // Multiple concerns - prioritize by severity
    const critical = concerns.filter(c => c.severity === 'critical');
    const urgent = concerns.filter(c => c.severity === 'urgent');
    
    if (critical.length > 0) {
      const metrics = critical.map(c => c.metric).join(', ');
      return `${metrics} are at critical levels. Contact your vet immediately.`;
    }
    
    if (urgent.length > 0) {
      const metrics = urgent.map(c => c.metric).join(', ');
      if (urgent.length === 1 && urgent[0].recommendation) {
        return `${metrics}: ${urgent[0].recommendation}`;
      }
      return `${metrics} need attention. Tap to see recommendations.`;
    }
    
    // All warnings
    const metrics = concerns.map(c => c.metric).join(', ');
    return `${metrics} are outside normal range. Review the analysis.`;
  }

  /**
   * Build detailed body text for a single health concern
   */
  private static buildSingleConcernBody(concern: HealthConcern): string {
    const { metric, trend, percentChange, recommendation, status } = concern;
    
    // If we have trend data, show it
    if (trend && percentChange !== undefined) {
      const direction = status === 'high' ? 'up' : 'down';
      const changeText = `${Math.abs(percentChange)}%`;
      
      if (trend === 'declining' && percentChange < 0) {
        // Getting worse
        if (recommendation) {
          return `${metric} is ${direction} ${changeText} and declining - ${recommendation}`;
        }
        return `${metric} is ${direction} ${changeText} from last test and declining`;
      }
      
      if (trend === 'improving' && percentChange > 0) {
        // Getting better but still abnormal
        return `${metric} improving (${direction} ${changeText}) but still needs monitoring`;
      }
      
      // Stable trend
      if (recommendation) {
        return `${metric} is ${status} (${direction} ${changeText}) - ${recommendation}`;
      }
      return `${metric} is ${status}, ${direction} ${changeText} from last test`;
    }
    
    // No trend data, just show recommendation or status
    if (recommendation) {
      return `${metric} is ${status} - ${recommendation}`;
    }
    
    return `${metric} is outside normal range (${status})`;
  }

  /**
   * Build "good news" notification body for improving results
   */
  static buildGoodNewsBody(concerns: HealthConcern[]): string {
    const improving = concerns.filter(c => c.trend === 'improving');
    
    if (improving.length === 0) {
      return 'All lab values returned to normal range! 🎉';
    }
    
    if (improving.length === 1) {
      const c = improving[0];
      if (c.percentChange !== undefined) {
        return `${c.metric} improved ${Math.abs(c.percentChange)}% and is now in normal range! 🎉`;
      }
      return `${c.metric} is now in normal range! 🎉`;
    }
    
    const metrics = improving.map(c => c.metric).join(', ');
    return `${metrics} all improved and are now in normal range! 🎉`;
  }

  /**
   * Determine overall health alert severity from concerns
   */
  static determineHealthSeverity(concerns: HealthConcern[]): HealthAlertSeverity {
    if (concerns.length === 0) return 'info';
    
    // Find highest severity
    const severities: HealthAlertSeverity[] = ['info', 'warning', 'urgent', 'critical'];
    let highest: HealthAlertSeverity = 'info';
    
    for (const concern of concerns) {
      const currentIndex = severities.indexOf(concern.severity);
      const highestIndex = severities.indexOf(highest);
      
      if (currentIndex > highestIndex) {
        highest = concern.severity;
      }
    }
    
    return highest;
  }

  /**
   * Calculate overall trend from multiple concerns
   */
  static calculateOverallTrend(
    concerns: HealthConcern[]
  ): 'improving' | 'stable' | 'declining' | undefined {
    if (concerns.length === 0) return undefined;
    
    const withTrends = concerns.filter(c => c.trend);
    if (withTrends.length === 0) return undefined;
    
    const improving = withTrends.filter(c => c.trend === 'improving').length;
    const declining = withTrends.filter(c => c.trend === 'declining').length;
    const stable = withTrends.filter(c => c.trend === 'stable').length;
    
    // Majority wins
    if (declining > improving && declining > stable) return 'declining';
    if (improving > declining && improving > stable) return 'improving';
    return 'stable';
  }

  /**
   * Calculate average percent change across concerns
   */
  static calculateAveragePercentChange(concerns: HealthConcern[]): number | undefined {
    const withChanges = concerns.filter(c => c.percentChange !== undefined);
    if (withChanges.length === 0) return undefined;
    
    const sum = withChanges.reduce((acc, c) => acc + (c.percentChange || 0), 0);
    return Math.round(sum / withChanges.length);
  }

  /**
   * Get action label based on severity
   */
  static getActionLabel(severity: HealthAlertSeverity): string {
    switch (severity) {
      case 'critical':
        return 'Contact Vet Now';
      case 'urgent':
        return 'View & Contact Vet';
      case 'warning':
        return 'View Details';
      case 'info':
        return 'View Results';
      default:
        return 'View';
    }
  }

  /**
   * Build batch notification title for consolidated updates
   */
  static buildBatchTitle(petName: string, eventCount: number, eventType?: DocumentEventType): string {
    if (eventCount === 1) {
      return `${petName} has a new ${this.getEventTypeLabel(eventType)} update`;
    }
    
    if (eventType) {
      return `${petName} has ${eventCount} new ${this.getEventTypeLabel(eventType)} updates`;
    }
    
    return `${petName} has ${eventCount} new health updates`;
  }

  /**
   * Build batch notification body
   */
  static buildBatchBody(summaries: string[]): string {
    if (summaries.length === 0) {
      return 'Tap to view updates';
    }
    
    if (summaries.length === 1) {
      return summaries[0];
    }
    
    if (summaries.length === 2) {
      return `${summaries[0]} and ${summaries[1]}`;
    }
    
    return `${summaries[0]}, ${summaries[1]}, and ${summaries.length - 2} more`;
  }

  /**
   * Get friendly label for event type
   */
  static getEventTypeLabel(eventType?: DocumentEventType): string {
    if (!eventType) return 'health record';
    
    const labels: Record<DocumentEventType, string> = {
      email_received: 'message',
      document_classified: 'document',
      lab_results_analyzed: 'lab result',
      vet_visit_analyzed: 'vet visit',
      expense_extracted: 'expense',
      appointment_detected: 'appointment',
      medication_extracted: 'medication',
      vaccination_extracted: 'vaccination'
    };
    
    return labels[eventType] || 'record';
  }

  /**
   * Build comprehensive health alert details from concerns
   */
  static buildHealthAlertDetails(
    concerns: HealthConcern[],
    labName?: string,
    testDate?: Date
  ): HealthAlertDetails {
    const severity = this.determineHealthSeverity(concerns);
    const trend = this.calculateOverallTrend(concerns);
    const percentChange = this.calculateAveragePercentChange(concerns);
    
    // Get primary recommendation (from highest severity concern)
    const sortedConcerns = [...concerns].sort((a, b) => {
      const severities: HealthAlertSeverity[] = ['info', 'warning', 'urgent', 'critical'];
      return severities.indexOf(b.severity) - severities.indexOf(a.severity);
    });
    
    const recommendedAction = sortedConcerns[0]?.recommendation;
    const veterinarianContactRecommended = severity === 'critical' || severity === 'urgent';
    
    return {
      severity,
      affectedMetrics: concerns.map(c => c.metric),
      concerns,
      trend,
      overallPercentChange: percentChange,
      recommendedAction,
      veterinarianContactRecommended,
      labName,
      testDate
    };
  }

  /**
   * Extract health concerns from Claude AI analysis
   * This parses your existing Claude analysis format AND UDP5.0 format.
   * `analysisResult` is genuinely heterogeneous (legacy Claude format vs UDP5
   * test_panels), narrowed inside.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static extractHealthConcerns(analysisResult: any): HealthConcern[] {
    const concerns: HealthConcern[] = [];
    
    try {
      // Check for healthConcerns in analysis
      if (analysisResult.healthConcerns && Array.isArray(analysisResult.healthConcerns)) {
        return analysisResult.healthConcerns;
      }
      
      // CHECK FOR UDP5.0 FORMAT (from backend processing)
      const labResult = analysisResult.labResult || analysisResult;
      
      // Extract from UDP5.0 test_panels structure
      if (labResult.test_panels && Array.isArray(labResult.test_panels)) {
        for (const panel of labResult.test_panels) {
          if (panel.individual_tests && Array.isArray(panel.individual_tests)) {
            for (const test of panel.individual_tests) {
              if (test.is_abnormal) {
                concerns.push(this.createConcernFromUDP5Test(test, labResult));
              }
            }
          }
        }
      }
      
      // Check for abnormal test results in lab data (UDP4.0 format)
      if (analysisResult.testResults && Array.isArray(analysisResult.testResults)) {
        for (const test of analysisResult.testResults) {
          if (test.status === 'high' || test.status === 'low' || test.status === 'critical') {
            concerns.push(this.createConcernFromTest(test, analysisResult));
          }
        }
      }
      
      // Check for flagged values
      if (analysisResult.flaggedValues && Array.isArray(analysisResult.flaggedValues)) {
        for (const flagged of analysisResult.flaggedValues) {
          concerns.push(this.createConcernFromFlagged(flagged));
        }
      }
      
      log.debug('Extracted health concerns from analysis', { concernCount: concerns.length });
    } catch (error) {
      log.error('Error extracting health concerns', error as Error);
    }
    
    return concerns;
  }

  /**
   * Create health concern from test result
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private static createConcernFromTest(test: any, analysisResult: any): HealthConcern {
    return {
      metric: test.testName || test.name || 'Unknown',
      currentValue: test.value || test.currentValue || 0,
      normalRange: test.normalRange || test.referenceRange,
      status: test.status,
      severity: this.mapStatusToSeverity(test.status, test.flags),
      trend: test.trend,
      percentChange: test.percentChange || test.changePercent,
      previousValue: test.previousValue,
      previousDate: test.previousDate ? new Date(test.previousDate) : undefined,
      recommendation: test.recommendation || analysisResult.recommendation,
      requiresVetContact: test.status === 'critical' || test.flags?.includes('CRITICAL')
    };
  }

  /**
   * Create health concern from flagged value
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private static createConcernFromFlagged(flagged: any): HealthConcern {
    return {
      metric: flagged.testName || flagged.metric || 'Unknown',
      currentValue: flagged.value || 0,
      normalRange: flagged.normalRange,
      status: flagged.status || 'high',
      severity: this.mapStatusToSeverity(flagged.status, flagged.flags),
      recommendation: flagged.recommendation || flagged.notes,
      requiresVetContact: flagged.severity === 'critical' || flagged.requiresVetContact
    };
  }

  /**
   * Create health concern from UDP5.0 test structure
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private static createConcernFromUDP5Test(test: any, _labResult: any): HealthConcern {
    // Determine severity based on urgency level and flag
    let severity: HealthAlertSeverity = 'info';
    
    if (test.urgency_level === 'Critical') {
      severity = 'critical';
    } else if (test.urgency_level === 'Urgent') {
      severity = 'urgent';
    } else if (test.flag === 'H' || test.flag === 'L') {
      severity = 'warning';
    }
    
    // Parse result_value if it's a number
    const currentValue = typeof test.result_value === 'string' 
      ? parseFloat(test.result_value.replace(/[^0-9.-]/g, '')) || 0
      : test.result_value || 0;
    
    return {
      metric: test.test_name || 'Unknown Test',
      currentValue,
      normalRange: test.reference_range || 'Unknown',
      status: test.flag === 'H' ? 'high' : 'low', // Abnormal tests are either high or low
      severity,
      recommendation: test.clinical_significance || '',
      requiresVetContact: severity === 'critical' || severity === 'urgent'
    };
  }

  /**
   * Map test status to health alert severity
   */
  private static mapStatusToSeverity(
    status: string, 
    flags?: string[]
  ): HealthAlertSeverity {
    if (status === 'critical' || flags?.includes('CRITICAL')) {
      return 'critical';
    }
    
    if (status === 'high' || status === 'low') {
      // Check if it's severely high/low based on flags
      if (flags?.includes('HIGH') || flags?.includes('LOW')) {
        return 'urgent';
      }
      return 'warning';
    }
    
    return 'info';
  }

  /**
   * Check if results are "good news" (previously abnormal, now normal)
   */
  static isGoodNews(
    currentConcerns: HealthConcern[], 
    previousHadAbnormal: boolean
  ): boolean {
    return previousHadAbnormal && currentConcerns.length === 0;
  }

  /**
   * Check if results have improving trends
   */
  static hasImprovingTrends(concerns: HealthConcern[]): boolean {
    return concerns.some(c => c.trend === 'improving');
  }
}

export default NotificationContentBuilder;

